/**
 * RadioOverlay — SENTINEL C2 Real-Time Radio Chatter Monitor
 *
 * Three visibility states:
 *   1. Hidden — completely removed from DOM (controlled by parent)
 *   2. Collapsed — thin 40px strip with severity indicator dots
 *   3. Expanded — 320px sidebar with full transcript + controls
 */

import { useState, useEffect, useRef, useCallback, type ChangeEvent } from 'react'
import type { RadioTranscript, RadioAnalysis, RadioSeverity } from '../../types'
import { useRadioFeed } from '../../hooks/useRadioFeed'
import { SEVERITY_COLORS } from '../../services/radioTagger'

interface RadioOverlayProps {
  onClose: () => void
}

export function RadioOverlay({ onClose }: RadioOverlayProps) {
  const [isExpanded, setIsExpanded] = useState(true)
  const [analysis, setAnalysis] = useState<RadioAnalysis | null>(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [filePath, setFilePath] = useState('audio/sample_checkin.m4a')

  const scrollRef = useRef<HTMLDivElement>(null)
  const autoScrollRef = useRef(true)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const {
    transcripts,
    isConnected,
    isTranscribing,
    statusMessage,
    startFile,
    startFileUpload,
    startMic,
    stop,
    clearTranscripts,
    analyzeRecent,
  } = useRadioFeed()

  // Auto-scroll to bottom on new transcripts
  useEffect(() => {
    if (autoScrollRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [transcripts])

  // Detect if user scrolled up (disable auto-scroll)
  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current
    autoScrollRef.current = scrollHeight - scrollTop - clientHeight < 50
  }, [])

  const handleAnalyze = useCallback(async () => {
    setIsAnalyzing(true)
    const result = await analyzeRecent()
    setAnalysis(result)
    setIsAnalyzing(false)
  }, [analyzeRecent])

  const handleFileStart = useCallback(() => {
    if (filePath.trim()) startFile(filePath.trim())
  }, [filePath, startFile])

  const handleFileUpload = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setFilePath(file.name)
      startFileUpload(file)
    }
    // Reset so the same file can be re-selected
    e.target.value = ''
  }, [startFileUpload])

  // Get recent severity counts for collapsed strip
  const severityCounts = transcripts.reduce<Record<RadioSeverity, number>>(
    (acc, t) => {
      acc[t.severity]++
      return acc
    },
    { CRITICAL: 0, WARNING: 0, INFO: 0, ROUTINE: 0 },
  )

  // Collapsed strip — thin right bar with severity dots
  if (!isExpanded) {
    return (
      <div
        className="w-10 border-l border-term-border bg-term-bg flex flex-col items-center py-3 gap-2 shrink-0 cursor-pointer"
        onClick={() => setIsExpanded(true)}
        title="Expand Radio Monitor"
      >
        <span className="text-[10px] text-term-textDim" style={{ writingMode: 'vertical-rl' }}>
          RADIO
        </span>
        <div className="flex flex-col gap-1.5 mt-2">
          {severityCounts.CRITICAL > 0 && (
            <SeverityDot severity="CRITICAL" count={severityCounts.CRITICAL} />
          )}
          {severityCounts.WARNING > 0 && (
            <SeverityDot severity="WARNING" count={severityCounts.WARNING} />
          )}
          {severityCounts.INFO > 0 && (
            <SeverityDot severity="INFO" count={severityCounts.INFO} />
          )}
        </div>
        <div className="mt-auto">
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: isConnected ? '#22c55e' : '#6b7280' }}
            title={isConnected ? 'Connected' : 'Disconnected'}
          />
        </div>
      </div>
    )
  }

  // Expanded sidebar
  return (
    <div className="w-80 border-l border-term-border bg-term-bg flex flex-col shrink-0 overflow-hidden">
      {/* Header */}
      <div className="px-3 py-2 border-b border-term-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-term-text tracking-wider">RADIO MONITOR</span>
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: isConnected ? '#22c55e' : '#6b7280' }}
            title={statusMessage}
          />
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsExpanded(false)}
            className="px-1.5 py-0.5 text-term-textDim hover:text-term-text text-xs transition-colors"
            title="Collapse"
          >
            &minus;
          </button>
          <button
            onClick={onClose}
            className="px-1.5 py-0.5 text-term-textDim hover:text-term-error text-xs transition-colors"
            title="Close Radio Monitor"
          >
            &times;
          </button>
        </div>
      </div>

      {/* Status */}
      <div className="px-3 py-1.5 text-[10px] text-term-textDim border-b border-term-border/50">
        {isConnected ? (
          <span className="text-term-success">&bull; {statusMessage}</span>
        ) : (
          <span className="text-term-textDim">&cir; {statusMessage}</span>
        )}
      </div>

      {/* Controls */}
      <div className="px-3 py-2 border-b border-term-border/50 flex flex-col gap-1.5">
        <div className="flex items-center gap-1">
          <input
            type="text"
            value={filePath}
            onChange={(e) => setFilePath(e.target.value)}
            placeholder="audio/file.m4a"
            className="flex-1 bg-term-bgDark border border-term-border rounded px-2 py-1 text-[11px] text-term-text placeholder-term-textDim focus:outline-none focus:border-term-primary"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={!isConnected || isTranscribing}
            className="px-2 py-1 text-[11px] font-medium bg-term-bgLight border border-term-border rounded hover:bg-term-primary hover:border-term-primary hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
            title="Browse for audio file"
          >
            ...
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*"
            onChange={handleFileUpload}
            className="hidden"
          />
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleFileStart}
            disabled={!isConnected || isTranscribing}
            className="flex-1 px-2 py-1 text-[11px] font-medium bg-term-bgLight border border-term-border rounded hover:bg-term-primary hover:border-term-primary hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            &#9654; FILE
          </button>
          <button
            onClick={startMic}
            disabled={!isConnected || isTranscribing}
            className="flex-1 px-2 py-1 text-[11px] font-medium bg-term-bgLight border border-term-border rounded hover:bg-term-error hover:border-term-error hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            &#9679; MIC
          </button>
          <button
            onClick={stop}
            disabled={!isConnected || !isTranscribing}
            className="flex-1 px-2 py-1 text-[11px] font-medium bg-term-bgLight border border-term-border rounded hover:bg-term-warning hover:border-term-warning hover:text-black disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            &#9632; STOP
          </button>
        </div>
      </div>

      {/* Transcript scroll area */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-2 py-1"
      >
        {transcripts.length === 0 ? (
          <div className="flex items-center justify-center h-full text-term-textDim text-xs">
            No transcripts yet. Start a file or mic capture.
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {transcripts.map((t) => (
              <TranscriptEntry key={t.id} transcript={t} />
            ))}
          </div>
        )}
      </div>

      {/* Footer: clear + count */}
      <div className="px-3 py-1 border-t border-term-border/50 flex items-center justify-between">
        <span className="text-[10px] text-term-textDim">{transcripts.length} entries</span>
        <button
          onClick={clearTranscripts}
          className="text-[10px] text-term-textDim hover:text-term-text transition-colors"
        >
          Clear
        </button>
      </div>

      {/* LLM Analysis section */}
      <div className="border-t border-term-border px-3 py-2">
        <button
          onClick={handleAnalyze}
          disabled={isAnalyzing || transcripts.length === 0}
          className="w-full px-2 py-1.5 text-[11px] font-medium bg-term-primary/20 border border-term-primary/50 rounded text-term-primaryBright hover:bg-term-primary/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {isAnalyzing ? 'ANALYZING...' : 'ANALYZE LAST 5 MIN'}
        </button>

        {analysis && (
          <div className="mt-2 p-2 bg-term-bgDark border border-term-border/50 rounded text-[11px]">
            <p className="text-term-text mb-1">{analysis.summary}</p>
            {analysis.callsigns.length > 0 && (
              <div className="mb-1">
                <span className="text-term-textDim">Callsigns: </span>
                <span className="text-term-info">{analysis.callsigns.join(', ')}</span>
              </div>
            )}
            {analysis.statusChanges.length > 0 && (
              <div className="mb-1">
                <span className="text-term-textDim">Status: </span>
                <span className="text-term-warning">{analysis.statusChanges.join('; ')}</span>
              </div>
            )}
            {analysis.threats.length > 0 && (
              <div>
                <span className="text-term-textDim">Threats: </span>
                <span className="text-term-error">{analysis.threats.join('; ')}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function TranscriptEntry({ transcript }: { transcript: RadioTranscript }) {
  const colors = SEVERITY_COLORS[transcript.severity]
  const time = new Date(transcript.timestamp * 1000)
  const timeStr = time.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })

  return (
    <div
      className="px-2 py-1.5 rounded text-[11px] leading-relaxed"
      style={{
        borderLeft: `3px solid ${colors.border}`,
        backgroundColor: colors.bg,
      }}
    >
      <div className="flex items-start justify-between gap-1">
        <span className="text-term-textDim shrink-0">{timeStr}</span>
        <span
          className="text-[9px] font-bold px-1 rounded shrink-0"
          style={{ color: colors.text }}
        >
          {transcript.severity}
        </span>
      </div>
      <p className="text-term-text mt-0.5">{transcript.text}</p>
      {transcript.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {transcript.tags.map((tag) => (
            <span
              key={tag}
              className="px-1.5 py-0 text-[9px] rounded-full border"
              style={{
                borderColor: colors.border,
                color: colors.text,
              }}
            >
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function SeverityDot({ severity, count }: { severity: RadioSeverity; count: number }) {
  const colors = SEVERITY_COLORS[severity]
  return (
    <div className="relative" title={`${count} ${severity}`}>
      <div
        className="w-3 h-3 rounded-full"
        style={{
          backgroundColor: colors.dot,
          boxShadow: severity === 'CRITICAL' ? `0 0 6px ${colors.dot}` : undefined,
        }}
      />
      {count > 1 && (
        <span
          className="absolute -top-1 -right-1.5 text-[8px] font-bold"
          style={{ color: colors.text }}
        >
          {count}
        </span>
      )}
    </div>
  )
}
